import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuList, MenuItem, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Preloader from 'components/Preloader';
import { CONTEXT_TYPES } from 'constants/menu';
import { sizeOverrideMatchesSize } from 'utils/menu';

const getOptionSizePrices = (item, productSizes, isOnePrice) => {
  if (isOnePrice) {
    return [];
  }
  const sizes = productSizes || [];
  if (!sizes.length) {
    return [];
  }
  const overrides = item?.priceOverride || [];
  return sizes
    .map((size) => {
      const candidates = overrides.filter(
        (override) =>
          override.context_type === CONTEXT_TYPES.modifierItem &&
          sizeOverrideMatchesSize(override, size)
      );
      // An option can hold more than one row for a size: one saved with the
      // item's size id, another from an edit made outside the item. The form
      // shows the priced one, so the list has to agree with it.
      const match =
        candidates.find((override) => Number(override.price)) || candidates[0];
      // A group priced against other sizes carries no row for this one. The
      // form hides that size, so falling back to the option's base price here
      // would show a price the group does not have.
      if (!match) {
        return null;
      }
      return {
        name: size.name,
        price: match.price ?? item?.price ?? 0,
      };
    })
    .filter((row) => row?.name);
};

const ShowModifierOptions = ({ loading, currentModifier, currentProduct }) => {
  const currentProductSizeTypeModifiersLists =
    currentProduct?.sizeTypeModifiers?.[0]?.list || [];
  const { t } = useTranslation();
  const defaultCount = 4;
  const [showCount, setShowCount] = useState(defaultCount);
  const [showBtnTxt, setShowBtnTxt] = useState(t('menu.show_more'));


  const handleShowMore = useCallback(() => {
    if (showBtnTxt.includes('more')) {
      setShowCount(currentModifier?.list?.length);
      setShowBtnTxt(t('menu.show_less'));
    } else {
      setShowCount(4);
      setShowBtnTxt(t('menu.show_more'));
    }
  }, [currentModifier?.list?.length, showBtnTxt, t]);

  const renderMenuItem = (item) => {
    const priceOverrideList = getOptionSizePrices(
      item,
      currentProductSizeTypeModifiersLists,
      currentModifier?.isOnePrice
    );

    return (
      <MenuItem
        sx={{
          color: '#0F1214',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          padding: '8px 16px 8px 45px',
          cursor: 'default',
          maxWidth: '800px',
        }}
        key={item?._id}
      >
        {item.name}{' '}
        <Box display="flex" ml={1.5}>
          {(() => {
            const priceParts = currentModifier.isOnePrice
              ? [Number(item.price).toFixed(2)]
              : (priceOverrideList || []).map(
                  (it) => `${it.name} : ${Number(it.price).toFixed(2)}`
                );
            return (
              <Typography
                sx={{
                  color: '#929398',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  mr: 0.5,
                }}
              >
                <Box component="span" mr={0.5}>
                  <FiberManualRecordIcon sx={{ fontSize: 5 }} />
                </Box>
                {priceParts.join(' , ')}
              </Typography>
            );
          })()}
        </Box>
      </MenuItem>
    );
  };

  return (
    <Box>
      {loading && <Preloader overlay={true} />}
      <MenuList autoFocusItem sx={{ p: 0, height: '100%', overflowY: 'auto' }}>
        {currentModifier?.list?.map((item, index) => {
          if (index < showCount && showBtnTxt.includes('more')) {
            return renderMenuItem(item);
          } else if (!showBtnTxt.includes('more')) {
            return renderMenuItem(item);
          }
        })}
        {currentModifier?.list?.length > defaultCount ? (
          <MenuItem
            onClick={handleShowMore}
            sx={{ textAlign: 'center', paddingTop: '11px', paddingBottom: '11px', justifyContent: 'center' }}
          >
            <Box
              component="button"
              sx={{
                background: 'none',
                border: 'none',
              }}
              type="button"
            >
              <Typography fontSize={14} fontStyle="normal" fontWeight={500} color="#0F1214">
                {showBtnTxt}
              </Typography>
            </Box>
          </MenuItem>
        ) : null}
      </MenuList>
    </Box>
  );
};

export default ShowModifierOptions;
